import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { X, Loader2, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';

const fmtCur = v => v == null ? '—' : `£${Number(v).toFixed(2)}`;
const fmtMeasure = (row) => {
  if (row.measure_type === 'count') return 'Whole unit (no serving size)';
  if (row.effective_selling_measure_ml == null) return '—';
  return `${row.effective_selling_measure_label || 'Serving'} (${row.effective_selling_measure_ml}ml)`;
};

const STATUS_BADGE = {
  new:            { label: 'New',             cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  match:          { label: 'Matches existing', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  conflict:       { label: 'Conflict',         cls: 'bg-amber-50 text-amber-700 border-amber-300' },
  blocked:        { label: 'Blocked',          cls: 'bg-red-50 text-red-700 border-red-200' },
  already_applied:{ label: 'Already applied',  cls: 'bg-blue-50 text-blue-700 border-blue-200' },
};

/**
 * PSB — final source-price & selling-measure review (2026-11).
 *
 * Opens from Stage 1B once every product match for this run is resolved
 * (coverage.held_count === 0) — NOT tied to Stage 7 valuation-lock or
 * Stage 8 dispatch, both of which stay separate concerns. Nothing is ever
 * written to Site Bar Prices just by opening this panel; every row needs
 * an explicit per-row choice before "Apply selected prices" writes
 * anything, and a 'blocked' (ambiguous source price / unresolved selling
 * measure) row can never be selected at all — it fails closed.
 */
export function PsbPriceReviewPanel({ s1RunId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [choices, setChoices] = useState({}); // beverage_product_id -> 'apply' | 'skip'
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/psb/price-review/${s1RunId}`);
      setData(res.data);
      // 2026-11 fix — never pre-select ANY row for Apply, including
      // non-conflicting 'new' ones. The product requirement is that
      // nothing is ever applied without the auditor EXPLICITLY choosing
      // it here — a sensible default that silently pre-ticks rows isn't
      // "explicit" even when there's nothing existing to conflict with.
      const defaults = {};
      for (const r of (res.data.rows || [])) {
        defaults[r.beverage_product_id] = 'skip';
      }
      setChoices(defaults);
    } catch (e) {
      toast.error('Could not load the price review', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [s1RunId]);

  useEffect(() => { load(); }, [load]);

  const setChoice = (pid, action) => setChoices(prev => ({ ...prev, [pid]: action }));

  const handleApply = async () => {
    const decisions = Object.entries(choices).map(([pid, action]) => ({ beverage_product_id: Number(pid), action }));
    const toApply = decisions.filter(d => d.action === 'apply').length;
    if (toApply === 0) {
      toast.info('Nothing selected to apply — mark at least one row "Apply" first.');
      return;
    }
    setApplying(true);
    try {
      const res = await api.post(`/psb/price-review/${s1RunId}/apply`, { decisions });
      setResult(res.data);
      toast.success(`Applied ${res.data.applied.filter(a => !a.already_applied).length} site price(s)`, {
        description: res.data.rejected.length ? `${res.data.rejected.length} row(s) could not be applied — see below.` : undefined,
      });
      await load(); // re-pull so already-applied/conflict states reflect reality
    } catch (e) {
      toast.error('Apply failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setApplying(false);
    }
  };

  const rows = data?.rows || [];
  const selectableCount = rows.filter(r => r.status === 'new' || r.status === 'conflict').length;
  const newOnlyCount = rows.filter(r => r.status === 'new').length;
  const selectAllNew = () => {
    // An explicit, deliberate bulk action (the auditor must click this),
    // not a silent default — only ever touches non-conflicting 'new'
    // rows, never a 'conflict' row (replacing an existing price always
    // needs its own individual decision).
    setChoices(prev => {
      const next = { ...prev };
      for (const r of rows) if (r.status === 'new') next[r.beverage_product_id] = 'apply';
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="psb-price-review-overlay">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Review source prices & selling measures</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Every match in this import is resolved — this is a one-time review before any price is written to
              Site Bar Prices. Nothing applies until you explicitly choose "Apply" per row and confirm below.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="psb-price-review-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Loading review…</div>
          ) : !data?.ready ? (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-4" data-testid="psb-price-review-not-ready">
              {data?.pending_count} product match{data?.pending_count === 1 ? '' : 'es'} still need a decision — this
              review isn't available until every match in this run is approved or excluded.
            </div>
          ) : data.source_unavailable ? (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-4" data-testid="psb-price-review-source-unavailable">
              The source file structure for this import doesn't support price extraction ({data.source_unavailable_reason}).
              No prices can be reviewed or applied from this import.
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No approved product matches found for this run.</div>
          ) : (
            <>
            {newOnlyCount > 0 && (
              <div className="mb-2">
                <button onClick={selectAllNew} className="text-xs text-emerald-700 underline hover:no-underline" data-testid="psb-price-review-select-all-new-btn">
                  Mark all {newOnlyCount} new (no existing price) rows as Apply
                </button>
              </div>
            )}
            <table className="w-full text-sm" data-testid="psb-price-review-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 pr-3 text-right">Source price / unit</th>
                  <th className="py-2 pr-3">Effective selling measure</th>
                  <th className="py-2 pr-3 text-right">Existing price</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Decision</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const badge = STATUS_BADGE[r.status] || STATUS_BADGE.new;
                  const canChoose = r.status === 'new' || r.status === 'conflict';
                  return (
                    <tr key={r.beverage_product_id} className="border-b border-slate-100" data-testid={`psb-price-review-row-${r.beverage_product_id}`}>
                      <td className="py-2 pr-3">
                        <div className="font-medium text-slate-800">{r.product_name}</div>
                        <div className="text-[11px] text-slate-400">PLU {r.source_plu_code} — {r.source_description}</div>
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {fmtCur(r.source_price_per_unit)}
                        {r.source_unit_label && <div className="text-[11px] text-slate-400">per {r.source_unit_label}</div>}
                        {r.measure_type === 'volume' && r.source_stock_unit_price != null && (
                          <div className="text-[10px] text-slate-400">({fmtCur(r.source_stock_unit_price)} per {r.source_stock_unit_label || 'stock unit'})</div>
                        )}
                      </td>
                      <td className="py-2 pr-3">{fmtMeasure(r)}</td>
                      <td className="py-2 pr-3 text-right">
                        {fmtCur(r.existing_price)}
                        {r.existing_effective_from && <div className="text-[11px] text-slate-400">from {r.existing_effective_from}</div>}
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded border ${badge.cls}`} data-testid={`psb-price-review-status-${r.beverage_product_id}`}>
                          {badge.label}
                        </span>
                        {r.status === 'blocked' && <div className="text-[11px] text-red-500 mt-1 max-w-[220px]">{r.blocked_reason}</div>}
                        {r.status === 'conflict' && (
                          <div className="text-[11px] text-amber-600 mt-1">Source would replace the existing price with a new, dated price row.</div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {canChoose ? (
                          <select
                            value={choices[r.beverage_product_id] || 'skip'}
                            onChange={e => setChoice(r.beverage_product_id, e.target.value)}
                            className="text-xs border border-slate-300 rounded px-2 py-1"
                            data-testid={`psb-price-review-decision-${r.beverage_product_id}`}
                          >
                            <option value="apply">Apply</option>
                            <option value="skip">Skip</option>
                          </select>
                        ) : (
                          <span className="text-[11px] text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </>
          )}

          {result && (
            <div className="mt-4 text-xs bg-slate-50 border border-slate-200 rounded p-3" data-testid="psb-price-review-result">
              <div className="flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle2 size={14} /> Applied {result.applied.filter(a => !a.already_applied).length}, already applied {result.applied.filter(a => a.already_applied).length}, skipped {result.skipped.length}, rejected {result.rejected.length}</div>
              {result.rejected.length > 0 && (
                <ul className="mt-1 text-amber-700 list-disc list-inside">
                  {result.rejected.map(r => <li key={r.beverage_product_id}>#{r.beverage_product_id}: {r.reason}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <div className="text-xs text-slate-500">
            {selectableCount > 0 ? `${selectableCount} row(s) awaiting your decision` : 'Nothing to decide — review only.'}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm px-3 py-1.5 border border-slate-300 rounded hover:bg-white" data-testid="psb-price-review-done-btn">
              Done
            </button>
            {data?.ready && !data?.source_unavailable && rows.length > 0 && (
              <button
                onClick={handleApply}
                disabled={applying}
                className="text-sm px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 flex items-center gap-1"
                data-testid="psb-price-review-apply-btn"
              >
                {applying ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                Apply selected prices
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
